import ReactDOMServer from "react-dom/server";
import styled from "styled-components";

import { colorsPalettes, mixins, rgba } from "@similarweb/styles";

import { ClosableItemColorMarker } from "app/components/compare/StyledComponent";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    percentDataFormat,
    numbersFilter,
    percentsFilter,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";
import { FlexRow, FlexColumn, CenteredFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const TableCellStyle = styled(CenteredFlexRow)`
    &:nth-child(1) {
        margin-right: 10px;
    }
`;

const TableRowStyle = styled(FlexRow)`
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    letter-spacing: 0.6px;
    padding: 7px 0;
    line-height: 24px;
    justify-content: space-between;
    &.total {
        border-top: 1px solid ${colorsPalettes.carbon[50]};
        padding-top: 13px;
        margin-top: 10px;
        font-weight: 500;
    }
`;

const TableCellDomainMarker = styled(ClosableItemColorMarker)<{ background: string }>`
    position: static;
    background-color: ${(p) => p.background};
    margin: 0 8px 0 0;
    flex-shrink: 0;
    width: 10px;
    height: 10px;
`;

const TableCellDomainName = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 230px;
`;

const TooltipContainer = styled(FlexColumn)`
    padding: 8px;
    max-width: 350px;
    min-width: 260px;
    color: ${rgba(colorsPalettes.carbon[500], 0.8)};
`;

const Title = styled.span`
    ${mixins.setFont({ $size: 16 })};
    font-weight: bold;
    margin-bottom: 10px;
`;

const AverageComponentWrapper = styled(TableRowStyle)`
    border-top: 1px solid ${colorsPalettes.carbon[50]};
    padding-top: 20px;
    margin-top: 20px;
`;

interface IPoint {
    point: { y: number; color: string };
    series: { color: string; name: string };
    key: { title: string };
}

interface IAllTrafficChannelsOverviewBarChartTooltipProps {
    points: IPoint[];
    dataFormat: string;
    isCompare: boolean;
    total: number;
}

const getAverageComponent = (pointsList) => {
    const avg = pointsList.reduce((sum, item) => sum + item.traffic, 0) / pointsList.length;

    return (
        <AverageComponentWrapper>
            <TableCellStyle>
                <TableCellDomainName>
                    {i18nFilter()("mmx.channelsoverview.bar.chart.tooltip.compare.average")}
                </TableCellDomainName>
            </TableCellStyle>
            <TableCellStyle>{avg ? numbersFilter(avg) : "N/A"}</TableCellStyle>
        </AverageComponentWrapper>
    );
};
const TableRow = ({ point, dataFormat, total, points }) => {
    const calcPercentage = () => {
        let totalChannel = 0;
        points.forEach((point) => {
            totalChannel += point.y;
        });
        return point.traffic ? percentsFilter((point.traffic / totalChannel) * 100) : "N/A";
    };
    const value = point.traffic
        ? dataFormat === percentDataFormat
            ? numbersFilter((point.traffic * total) / 100)
            : numbersFilter(point.traffic)
        : "N/A";
    return (
        <TableRowStyle>
            <TableCellStyle>
                <TableCellDomainMarker background={point.color} />
                <TableCellDomainName>{point.name}</TableCellDomainName>
            </TableCellStyle>
            <TableCellStyle>{dataFormat === percentDataFormat ? calcPercentage() : value}</TableCellStyle>
        </TableRowStyle>
    );
};

export const AllTrafficChannelsOverviewBarChartTooltip = ({
    points,
    dataFormat,
    isCompare,
    total,
}: IAllTrafficChannelsOverviewBarChartTooltipProps) => {
    const pointsList = points.map((point) => {
        return {
            traffic: point.point.y,
            color: point.point.color,
            name: point.series.name,
            title: point.key.title,
        };
    });
    const calcTotal = () => {
        let totalChannel = 0;
        pointsList.forEach((point) => {
            totalChannel += point.traffic;
        });
        return dataFormat === percentDataFormat
            ? numbersFilter((totalChannel * total) / 100)
            : numbersFilter(totalChannel);
    };
    const isSingleSeries = pointsList.length === 1;

    return ReactDOMServer.renderToString(
        <TooltipContainer>
            <TableRowStyle>
                <Title>{points[0]?.key?.title}</Title>
            </TableRowStyle>
            {isSingleSeries ? (
                <TableRowStyle>
                    <TableCellStyle>
                        <TableCellDomainName>{points[0]?.key?.title} {i18nFilter()("utils.visits")}</TableCellDomainName>
                    </TableCellStyle>
                    <TableCellStyle>{calcTotal() === 0 ? "N/A" : calcTotal()}</TableCellStyle>
                </TableRowStyle>
            ) : (
                <>
                    {pointsList.map((pointData, index) => (
                        <TableRow
                            dataFormat={dataFormat}
                            total={total}
                            point={pointData}
                            key={index}
                            points={points}
                        />
                    ))}
                    <TableRowStyle className={"total"}>
                        <TableCellStyle>Total {points[0]?.key?.title} {i18nFilter()("utils.visits")}</TableCellStyle>
                        <TableCellStyle>{calcTotal() === 0 ? "N/A" : calcTotal()} </TableCellStyle>
                    </TableRowStyle>
                </>
            )}
        </TooltipContainer>,
    );
};
